import { useState } from "react"
import styled from "styled-components"

import { SparklesIcon } from "@minuthq/meatball-ui-icons"
import {
  Banner,
  Button,
  cardBoxShadow,
  Checkbox,
  Select,
  spacing,
  TextArea,
  TSelectOptions,
} from "@minuthq/meatball-ui-react"

import { GuestChatMoreKnowledgeDialog } from "src/components/GuestInbox/GuestChat/GuestChatMoreKnowledgeDialog"
import { MessageProps } from "src/components/GuestInbox/GuestChat/Message"
import { useFetchGuestMessageSuggestion } from "src/data/guests/queries/guestQueries"
import { TGuestMessage } from "src/data/guests/types/guestTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { ChatPanel } from "src/ui/ChatPanel/ChatPanel"
import { GlowingButton } from "src/ui/GlowingButton/GlowingButton"
import { MTooltip } from "src/ui/MTooltip/MTooltip"

type TMessageSender = "user" | "guest"

export function GuestChat({
  orgId,
  guestId,
  messages,
  onSendMessage,
  isSending,
  onLoadMore,
  isLoadingMore,
  showSendMessageSelector = false,
  showSendAsSelector = false,
  disableSendMessage = false,
  disabledTooltipContent,
}: {
  orgId: string
  guestId: string
  messages: TGuestMessage[]
  onSendMessage: (args: {
    message: string
    sendAs: TMessageSender
    autoResolve?: boolean
    shouldSendMessage?: boolean
  }) => void
  isSending: boolean
  channel: string
  onLoadMore: () => void
  isLoadingMore: boolean
  showSendAsSelector?: boolean
  showSendMessageSelector?: boolean
  disableSendMessage?: boolean
  disabledTooltipContent?: string
}) {
  const { tPlaceholder } = useTranslate()

  const [message, setMessage] = useState("")

  // Configuration state
  const [sendAs, setSendAs] = useState<TMessageSender>("user")
  const [shouldSendMessage, setShouldSendMessage] = useState(false)
  const [showMoreKnowledgeDialog, setShowMoreKnowledgeDialog] = useState(false)

  const fetchGuestMessageSuggestion = useFetchGuestMessageSuggestion()

  function sendMessage() {
    onSendMessage({
      message,
      sendAs,
      shouldSendMessage: showSendAsSelector ? shouldSendMessage : undefined,
    })
    setMessage("")
  }

  function handleInputKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (disableSendMessage || !message) {
      return
    }

    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      sendMessage()
    }
  }

  function generateAnswer() {
    fetchGuestMessageSuggestion.mutate(
      {
        organization_id: orgId,
        guest_id: guestId,
      },
      {
        onSuccess: (data) => {
          setMessage(data.suggestion)
        },
        onError: (data) => {
          if (data.response?.data.error_key === "knowledge_base_empty") {
            setShowMoreKnowledgeDialog(true)
          }
        },
      }
    )
  }

  const isMessageSuggestionError =
    fetchGuestMessageSuggestion.isError &&
    fetchGuestMessageSuggestion.error.response?.data.error_key &&
    fetchGuestMessageSuggestion.error.response.data.error_key !==
      "knowledge_base_empty"

  function renderInput() {
    return (
      <InputWrapper>
        {(showSendAsSelector || showSendMessageSelector) && (
          <ConfigurationWrapper>
            {showSendAsSelector && (
              <Select
                label="Send as"
                options={
                  [
                    { label: "User", value: "user" },
                    { label: "Guest", value: "guest" },
                  ] satisfies TSelectOptions<TMessageSender>
                }
                value={sendAs}
                onChange={(value) => setSendAs(value as TMessageSender)}
              />
            )}

            {showSendMessageSelector && (
              <Checkbox
                label="Send message"
                checked={shouldSendMessage}
                onCheck={(checked) => setShouldSendMessage(checked)}
              />
            )}
          </ConfigurationWrapper>
        )}

        {isMessageSuggestionError && (
          <Banner type="error" fullWidth>
            {tPlaceholder("Failed to generate answer")}
          </Banner>
        )}

        <TextArea
          minRows={4}
          value={message}
          onChange={setMessage}
          onKeyDown={handleInputKeyDown}
        />

        <InputBottomWrapper>
          <GenerateAnswerWrapper>
            <GlowingButton
              onClick={generateAnswer}
              StartIcon={SparklesIcon}
              loading={fetchGuestMessageSuggestion.isPending}
            >
              {tPlaceholder("Generate answer")}
            </GlowingButton>
          </GenerateAnswerWrapper>
          <SendWrapper>
            <MTooltip
              title={disableSendMessage ? disabledTooltipContent : undefined}
            >
              <Button
                color="neutral"
                size="small"
                loading={isSending}
                onClick={sendMessage}
                disabled={disableSendMessage || !message}
                fullWidth
              >
                {tPlaceholder("Send")}
              </Button>
            </MTooltip>
          </SendWrapper>
        </InputBottomWrapper>
      </InputWrapper>
    )
  }

  return (
    <>
      <ChatPanel
        messages={messages.map((message) => ({
          id: message.id,
          sentAt: message.timestamp,
          content: message.content,
          source: message.communication_channel,
          senderType:
            message.sender_type === "user" || message.sender_type === "minut_ai"
              ? "self"
              : "other",
          badges: getBadges(message, tPlaceholder),
          senderName:
            message.sender_type !== "minut_ai"
              ? message.sender_name
              : undefined,
        }))}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        renderInput={renderInput}
      />

      {showMoreKnowledgeDialog && (
        <GuestChatMoreKnowledgeDialog
          onClose={() => setShowMoreKnowledgeDialog(false)}
        />
      )}
    </>
  )
}

function getBadges(
  message: TGuestMessage,
  tPlaceholder: ReturnType<typeof useTranslate>["tPlaceholder"]
): MessageProps["badges"] {
  const badges: MessageProps["badges"] = []

  if (message.sender_type === "minut_ai") {
    badges.push({
      color: "promotional",
      label: tPlaceholder("Autopilot"),
      size: "small",
    })
  }

  if (message.is_escalation_message) {
    badges.push({
      color: "warning",
      label: tPlaceholder("Escalation"),
      size: "small",
      border: true,
    })
  }

  return badges
}

const InputWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
  padding: ${spacing.M};
  margin: 0 ${spacing.M} ${spacing.M} ${spacing.M};
  border-radius: 1rem;
  ${cardBoxShadow};
`

const InputBottomWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.L};
  margin-top: ${spacing.XS};
`

const GenerateAnswerWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`

const SendWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const ConfigurationWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${spacing.XS};
`
